import React, { useState } from "react";
import { X, Package, Code, Sparkles, Search } from "lucide-react";

interface CustomWidget {
  id: string;
  icon: React.ComponentType<any>;
  label: string;
  tooltip: string;
  w: number;
  h: number;
  module?: string;
}

interface CustomWidgetLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  customWidgets?: CustomWidget[];
  onSelectWidget?: (widgetId: string) => void;
}

export function CustomWidgetLibraryModal({
  isOpen,
  onClose,
  customWidgets = [],
  onSelectWidget,
}: CustomWidgetLibraryModalProps) {
  const [search, setSearch] = useState("");
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  if (!isOpen) return null;

  // Get unique modules
  const modules = Array.from(
    new Set(customWidgets.map((w) => w.module).filter(Boolean))
  ) as string[];

  // Filter widgets
  const filteredWidgets = customWidgets.filter((widget) => {
    const matchesSearch =
      widget.label.toLowerCase().includes(search.toLowerCase()) ||
      widget.tooltip.toLowerCase().includes(search.toLowerCase());
    const matchesModule = !selectedModule || widget.module === selectedModule;
    return matchesSearch && matchesModule;
  });

  // Group widgets by module
  const groupedWidgets = filteredWidgets.reduce((acc, widget) => {
    const module = widget.module || "Other";
    if (!acc[module]) acc[module] = [];
    acc[module].push(widget);
    return acc;
  }, {} as Record<string, CustomWidget[]>);

  const handleWidgetClick = (widgetId: string) => {
    if (onSelectWidget) {
      onSelectWidget(widgetId);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Package
                size={24}
                className="text-green-600 dark:text-green-400"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Custom Widget Library
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {customWidgets.length} module-specific widgets available
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search and Filter - Only show if there are widgets */}
        {customWidgets.length > 0 && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="search"
                  placeholder="Search widgets..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              {modules.length > 1 && (
                <select
                  value={selectedModule || ""}
                  onChange={(e) => setSelectedModule(e.target.value || null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">All Modules</option>
                  {modules.map((module) => (
                    <option key={module} value={module}>
                      {module.charAt(0).toUpperCase() + module.slice(1)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {customWidgets.length === 0 ? (
            /* Coming Soon State */
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-gradient-to-br from-green-100 to-blue-100 dark:from-green-900/30 dark:to-blue-900/30 rounded-full mb-6">
                <Sparkles
                  size={48}
                  className="text-green-600 dark:text-green-400"
                />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Custom Widget Builder
              </h3>

              <p className="text-gray-600 dark:text-gray-400 max-w-xl mb-6">
                Create custom dashboard widgets based on your module schemas.
                Define data sources, visualizations, and interactions tailored
                to your business needs.
              </p>

              <div className="w-full max-w-2xl bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Planned Features:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded">
                      <Code
                        size={16}
                        className="text-green-600 dark:text-green-400"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Module Integration
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Connect to your existing modules and schemas
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded">
                      <Package
                        size={16}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Widget Templates
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Pre-built templates for common use cases
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded">
                      <Sparkles
                        size={16}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        AI-Powered Builder
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Generate widgets from natural language
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded">
                      <Code
                        size={16}
                        className="text-orange-600 dark:text-orange-400"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Custom Code
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Write custom React components
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
                  📖 No custom widgets yet
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Check{" "}
                  <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 rounded">
                    docs/CUSTOM_WIDGETS_GUIDE.md
                  </code>{" "}
                  to learn how to create module-specific widgets
                </p>
              </div>
            </div>
          ) : filteredWidgets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                No widgets found matching your search.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedWidgets).map(([module, widgets]) => (
                <div key={module}>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide flex items-center gap-2">
                    <Package size={16} />
                    {module}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {widgets.map((widget) => {
                      const Icon = widget.icon;
                      return (
                        <button
                          key={widget.id}
                          onClick={() => handleWidgetClick(widget.id)}
                          className="flex items-start gap-3 p-4 bg-white dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900/20 border-2 border-gray-200 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-500 rounded-lg transition-all group text-left"
                        >
                          <div className="p-2 bg-gray-100 dark:bg-gray-600 group-hover:bg-green-100 dark:group-hover:bg-green-900/30 rounded-lg transition-colors flex-shrink-0">
                            <Icon
                              size={24}
                              className="text-gray-600 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                              {widget.label}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                              {widget.tooltip}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-400">
                                {widget.w}×{widget.h} grid
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {customWidgets.length > 0
              ? "Click a widget to add it to your dashboard"
              : "Modules can register custom widgets"}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-semibold transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
